import { supabase } from './supabaseClient';
import { normalizeQaResult, trimText } from '../utils/normalization';

const PRIMARY_QUALITY_MILESTONES = ['Initial Project Delivery', 'Restamp Required'];

function missingColumnFromError(error) {
  const message = String(error?.message || error?.details || '');
  const patterns = [
    /column (?:[a-zA-Z0-9_]+\.)?([a-zA-Z0-9_]+) does not exist/i,
    /Could not find the ['"]([^'"]+)['"] column/i,
    /column ['"]([^'"]+)['"] of relation/i,
  ];
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match?.[1]) return match[1];
  }
  return '';
}

function isMissingColumnError(error) {
  const message = String(error?.message || error?.details || '').toLowerCase();
  return error?.code === '42703'
    || message.includes('does not exist')
    || message.includes('could not find')
    || message.includes('schema cache');
}

function extractQaResultFromRaw(rawRowData) {
  if (!rawRowData || typeof rawRowData !== 'object') return '';

  const direct = rawRowData['Milestone QA Result']
    || rawRowData['milestone_qa_result']
    || rawRowData.MilestoneQAResult
    || rawRowData.AU;
  if (trimText(direct)) return trimText(direct);

  const entry = Object.entries(rawRowData).find(([key]) => {
    const normalizedKey = String(key || '').replace(/[\s_:-]+/g, '').toLowerCase();
    return normalizedKey === 'milestoneqaresult' || normalizedKey.endsWith('milestoneqaresult');
  });
  return entry ? trimText(entry[1]) : '';
}

function withQaResult(row) {
  const qa = trimText(row?.milestone_qa_result_norm)
    || trimText(row?.milestone_qa_result)
    || extractQaResultFromRaw(row?.raw_row_data);

  return {
    ...row,
    milestone_qa_result: qa || row?.milestone_qa_result || null,
    milestone_qa_result_norm: normalizeQaResult(qa || row?.milestone_qa_result_norm || row?.milestone_qa_result) || null,
  };
}

function isNoError(row) {
  const qa = trimText(row?.milestone_qa_result_norm || row?.milestone_qa_result);
  return qa.replace(/[\s_-]+/g, '').toLowerCase() === 'noerror';
}

function isBlankQa(row) {
  return !trimText(row?.milestone_qa_result_norm || row?.milestone_qa_result);
}

function buildDesignQualitySummary(rows) {
  const qualityRows = (rows || [])
    .map(withQaResult)
    .filter((row) => PRIMARY_QUALITY_MILESTONES.includes(row.milestone_type))
    .filter((row) => trimText(row.design_engineer_norm));

  const summarize = (scopedRows) => {
    const noError = scopedRows.filter(isNoError).length;
    const blank = scopedRows.filter(isBlankQa).length;
    const total = scopedRows.length;
    return {
      total_count: total,
      no_error_count: noError,
      blank_count: blank,
      error_count: Math.max(0, total - noError - blank),
      no_error_percent: total ? Math.round((noError * 1000) / total) / 10 : 0,
    };
  };

  const byMilestone = PRIMARY_QUALITY_MILESTONES.map((milestoneType) => ({
    milestone_type: milestoneType,
    ...summarize(qualityRows.filter((row) => row.milestone_type === milestoneType)),
  })).filter((row) => row.total_count > 0);

  const memberMap = new Map();
  qualityRows.forEach((row) => {
    const name = trimText(row.design_engineer_norm);
    if (!name) return;
    const current = memberMap.get(name) || {
      design_engineer: name,
      rows: [],
    };
    current.rows.push(row);
    memberMap.set(name, current);
  });

  const byMember = [...memberMap.values()].map((entry) => ({
    design_engineer: entry.design_engineer,
    ...summarize(entry.rows),
  })).sort((a, b) => b.total_count - a.total_count || a.design_engineer.localeCompare(b.design_engineer, undefined, { sensitivity: 'base' }));

  const totals = summarize(qualityRows);

  return {
    designQualityByMilestone: byMilestone,
    designEngineerQualitySummary: byMember,
    designQualityNoErrorPercent: totals.no_error_percent,
    designQualityTotal: totals.total_count,
    designQualityNoError: totals.no_error_count,
    designQualityBlank: totals.blank_count,
  };
}

function mergeQualityKpi(summary) {
  // Dashboard top KPI cards intentionally exclude Design Quality.
  return (summary?.kpis || []).filter((item) => item.key !== 'design_quality_no_error_percent');
}

export async function getDashboardSummary(uploadId, startDate, endDate) {
  if (!uploadId) return null;
  const { data, error } = await supabase.rpc('get_dashboard_summary', {
    p_upload_id: uploadId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw error;

  const summary = data || {};

  try {
    // Always calculate Design Quality from row-level data so Milestone QA Result can
    // be read from raw_row_data when the clean QA columns were added after an upload.
    // This prevents the dashboard from showing 100% Error / Other while the
    // Individual Performance detail table correctly shows No Error values.
    const qualityRows = await getTeamMemberDetailRows(uploadId, startDate, endDate);
    const quality = buildDesignQualitySummary(qualityRows);
    return {
      ...summary,
      kpis: mergeQualityKpi(summary),
      designQualityByMilestone: quality.designQualityByMilestone,
      designEngineerQualitySummary: quality.designEngineerQualitySummary,
      designQualityNoErrorPercent: quality.designQualityNoErrorPercent,
      designQualityTotal: quality.designQualityTotal,
      designQualityNoError: quality.designQualityNoError,
      designQualityBlank: quality.designQualityBlank,
    };
  } catch (fallbackError) {
    console.warn('Unable to calculate client-side design quality fallback', fallbackError);
    return {
      ...summary,
      kpis: mergeQualityKpi(summary),
    };
  }
}

export async function getTeamMemberPerformance(uploadId, startDate, endDate) {
  if (!uploadId) return [];
  const { data, error } = await supabase.rpc('get_team_member_performance', {
    p_upload_id: uploadId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw error;
  return data || [];
}

export async function getStateSummary(uploadId, startDate, endDate) {
  if (!uploadId) return [];
  const { data, error } = await supabase.rpc('get_state_summary', {
    p_upload_id: uploadId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw error;
  return data || [];
}

export async function getClientSummary(uploadId, startDate, endDate) {
  if (!uploadId) return [];
  const { data, error } = await supabase.rpc('get_client_summary', {
    p_upload_id: uploadId,
    p_start_date: startDate,
    p_end_date: endDate,
  });
  if (error) throw error;
  return data || [];
}

const TEAM_MEMBER_DETAIL_BASE_SELECT = `
  id,
  upload_id,
  source_row_number,
  project_name,
  project_key,
  milestone_type,
  client,
  state,
  service_type,
  service_type_norm,
  in_house_vs_external,
  milestone_date,
  design_engineer_norm,
  pe_engineer_norm,
  ee_engineer_norm,
  permit_applicant_norm,
  senior_designer_norm,
  design_reviewer_norm,
  ee_reviewer_norm,
  partner_engineer_norm,
  ready_for_design_tat_hours,
  ready_for_pe_tat_hours,
  ready_for_ee_tat_hours,
  ee_tat_hours,
  total_tat_hours,
  cre_restamp_reason,
  raw_row_data
`;

const OPTIONAL_TEAM_MEMBER_DETAIL_COLUMNS = [
  'milestone_qa_result',
  'milestone_qa_result_norm',
  'design_review_in_progress_tat_hours',
  'design_in_progress_tat_hours',
  'pe_in_progress_tat_hours',
  'ee_in_progress_tat_hours',
  'ready_for_ee_tat_hours',
  'ee_tat_hours',
  'cre_restamp_reason',
];

function buildTeamMemberDetailSelect(optionalColumns = OPTIONAL_TEAM_MEMBER_DETAIL_COLUMNS) {
  return [TEAM_MEMBER_DETAIL_BASE_SELECT, optionalColumns.join(',')].filter(Boolean).join(',');
}

async function fetchTeamMemberDetailRowsWithSelect(uploadId, startDate, endDate, selectClause) {
  const pageSize = 1000;
  let from = 0;
  const rows = [];

  while (true) {
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from('project_milestone_clean')
      .select(selectClause)
      .eq('upload_id', uploadId)
      .gte('milestone_date', startDate)
      .lte('milestone_date', endDate)
      .order('source_row_number', { ascending: true })
      .range(from, to);

    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
    from += pageSize;
  }

  return rows.map(withQaResult);
}

export async function getTeamMemberDetailRows(uploadId, startDate, endDate) {
  if (!uploadId) return [];

  let optionalColumns = [...OPTIONAL_TEAM_MEMBER_DETAIL_COLUMNS];
  const removedColumns = new Set();

  while (true) {
    try {
      return await fetchTeamMemberDetailRowsWithSelect(uploadId, startDate, endDate, buildTeamMemberDetailSelect(optionalColumns));
    } catch (error) {
      if (!isMissingColumnError(error)) throw error;
      const missingColumn = missingColumnFromError(error);
      const matchingOptional = optionalColumns.find((column) => column === missingColumn || column.includes(missingColumn) || missingColumn.includes(column));
      if (!matchingOptional || removedColumns.has(matchingOptional)) throw error;
      removedColumns.add(matchingOptional);
      optionalColumns = optionalColumns.filter((column) => column !== matchingOptional);
      console.warn(`${matchingOptional} is not present yet. Falling back without that optional field.`, error);
    }
  }
}
